import React, { useEffect, useState } from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import Lightbox from "react-image-lightbox";
import BackButton from '../../../components/custom/BackButton';
import ActionIcon from '../../../components/custom/ActionIcon';
import { createdUpdatedInfo, formatBoolean, formatDatetime, formatEmail } from '../../../utils';
import { IssueReport, IssueReportStatus, SelectOption } from '../../../globals/interfaces';
import Select from '../../../components/Select';
import { Input } from 'reactstrap';
import { AssignIssue, UpdateIssue } from '.';
import VirtualTable from '../../../components/custom/VirtualTable';
import useModal from '../../../customHooks/useModal';
import NotesModal from './NotesModal';
import _ from 'lodash';
import AddNotesModal from './AddNotesModal';
import { users } from './constants';
import { Nav } from 'reactstrap';
import CustomNavItem from '../../../components/custom/CustomNavItem';
import { TabContent } from 'reactstrap';
import CustomTabPane from '../../../components/custom/CustomTabPane';
import useFiles from './useFiles';
import Files from './Files';
import { Table } from 'reactstrap';

const userOptions: Array<SelectOption> = users.map(item => ({ label: item.toUpperCase(), value: item.toUpperCase() }));

type Tab = 'NOTES' | 'FILES';

interface Props {
    issue: IssueReport | null;
    modal: boolean;
    setModal: (state: boolean) => void;
    updateIssue: UpdateIssue;
    assign: AssignIssue;
}

export default function DetailsModal({
    issue,
    modal,
    setModal,
    updateIssue,
    assign
}: Props) {

    const { files, uploadFile, deleteFile } = useFiles(_.get(issue, 'guid', ''));
    const [viewScreenshot, setViewScreenshot] = useState(false);
    const [noteId, setNoteId] = useState<number>();
    const { modal: viewNotesModal, setModal: setViewNotesModal } = useModal();
    const { modal: addNotesModal, setModal: setAddNotesModal } = useModal();
    const [activeTab, setActiveTab] = useState<Tab>('NOTES');

    const selectedNote = _.get(issue, 'notes', []).find(item => item.id === noteId);

    useEffect(() => {
        if (!issue) return;
    }, [issue]);

    useEffect(() => {
        setActiveTab('NOTES');
        setNoteId(undefined);
        setViewScreenshot(false);
    }, [modal]);

    const toggleTab = (tab: Tab) => {
        setActiveTab(tab);
    };

    const toggle = () => setModal(!modal);

    const viewNote = (id: number) => {
        setViewNotesModal(true);
        setNoteId(id);
    }

    if (!issue) {
        return null;
    }

    const dataArray: Array<{ name: string; value: string | number }> = [{
        name: 'Unit',
        value: issue.unit
    }, {
        name: 'Impact',
        value: issue.severity
    }, {
        name: 'Page',
        value: issue.page
    }, {
        name: 'Can complete task',
        value: formatBoolean(issue.completeTask)
    }, {
        name: 'Num Affected',
        value: issue.numAffected
    }, {
        name: 'Issue Start Date',
        value: formatDatetime(issue.startDate)
    }, {
        name: 'Submitted on',
        value: formatDatetime(issue.createdAt)
    }, {
        name: 'Submitted by',
        value: formatEmail(issue.createdBy)
    }];


    return (
        <CustomModal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <TitleContainer>
                        <BackButton onClick={toggle} />
                        <h4 className={'pl-2'}>Issue #{issue.id} submitted by {formatEmail(issue.createdBy)}</h4>
                    </TitleContainer>
                    <Select
                        options={userOptions}
                        value={_.get(issue, 'assignedTo', '')}
                        onChange={option => assign(option.value)}
                        width={200}
                        label='Assigned To:'
                    />
                </HeaderContainer>
            </ModalHeader>
            <CustomModalBody>
                <div>
                    <Table>
                        <thead className={'table-success'}>
                            <tr>
                                {
                                    dataArray.map((item, i) => (
                                        <th key={i}>{item.name}</th>
                                    ))
                                }
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                {
                                    dataArray.map((item, i) => (
                                        <td key={i}>{item.value}</td>
                                    ))
                                }
                            </tr>
                        </tbody>
                    </Table>
                    <Table>
                        <thead className={'table-success'}>
                            <tr>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{issue.details}</td>
                            </tr>
                        </tbody>
                    </Table>
                </div>
                <Nav tabs className={'separator-tabs'}>
                    <CustomNavItem<Tab>
                        tabName={`Notes (${issue.notes.length})`}
                        tabId="NOTES"
                        activeTabId={activeTab}
                        toggleTab={toggleTab}
                    />
                    <CustomNavItem<Tab>
                        tabName={`Files (${files.length})`}
                        tabId="FILES"
                        activeTabId={activeTab}
                        toggleTab={toggleTab}
                    />
                </Nav>
                <TabContent activeTab={activeTab} className={'mt-2'}>
                    <CustomTabPane<Tab> tabId={'NOTES'}>
                        <FlexContainer>
                            <b>Notes</b>
                            <ActionIcon
                                type={'add'}
                                onClick={() => setAddNotesModal(true)}
                            />
                        </FlexContainer>
                        <div>
                            <VirtualTable
                                data={issue.notes}
                                mapping={[{
                                    name: 'Date',
                                    value: 'createdAt',
                                    datetime: true,
                                    utc: true
                                }, {
                                    name: 'Note',
                                    value: 'note'
                                }, {
                                    name: 'User',
                                    value: 'createdBy',
                                    email: true
                                }]}
                                numRows={10}
                                handleClick={item => viewNote(item.id)}
                            />
                        </div>
                    </CustomTabPane>
                    <CustomTabPane<Tab> tabId={'FILES'}>
                        <Files
                            files={files}
                            uploadFile={uploadFile}
                            deleteFile={deleteFile}
                        />
                    </CustomTabPane>
                </TabContent>

            </CustomModalBody>
            {
                viewScreenshot && (
                    <Lightbox
                        mainSrc={issue.screenshot}
                        onCloseRequest={() => setViewScreenshot(false)}
                    />
                )
            }
            <NotesModal
                item={selectedNote}
                modal={viewNotesModal}
                setModal={setViewNotesModal}
            />
            <AddNotesModal
                modal={addNotesModal}
                setModal={setAddNotesModal}
                issue={issue}
                updateIssue={updateIssue}
            />
        </CustomModal>
    );
}

const CustomModal = styled(Modal)`
    max-width: 1200px;
`;

const CustomModalBody = styled(ModalBody)`
    p {
        margin-bottom: 0px;
    }
`;

export const FlexContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
`;

const TitleContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const Screenshot = styled.img`
    object-fit: contain;
    height: 150px;
    width: auto;
    &:hover {
        cursor: pointer;
    }
`;
